<template>
  <FreeBottomSheet :name="name" @onOpen="onOpen" @onClose="onClose">
    <template #header="{ hide }">
      <FreeHeader class="p-4">
        <template #default>
          <h2 class="text-base font-medium">Dev auth</h2>
        </template>
        <template #rightAction>
          <FreeCloseButton @click="hide" />
        </template>
      </FreeHeader>
    </template>
    <template #default>
      <div
        class="bg-background-neutral-secondary mx-4 mb-4 flex min-h-[200px] flex-col space-y-4 rounded-2xl p-4"
      >
        <FreeLoader v-if="isLoading" class="m-auto h-fit" />
        <template v-else>
          <BooleanOption
            label="Dev авторизация"
            :value="isDevAuth"
            @onChange="
              draftState.updateDraft({
                isDevAuth: !draftState.state.value.isDevAuth,
              })
            "
          />
          <form class="space-y-2" @submit="submitForm">
            <FreeInput
              v-maska:[phoneMask]
              :has-error="!isValidPhone"
              :model-value="draftState.state.value.phone"
              :disabled="!draftState.state.value.isDevAuth"
              type="tel"
              label="Номер телефона для авторизации"
              placeholder="+7 777 123 45 67"
              clearable
              @update:model-value="draftState.updateDraft({ phone: $event })"
            >
              <template #clearButton>
                <FreeCloseButton class="clear-input-button" />
              </template>
            </FreeInput>
            <FreeInput
              v-maska:[iinMask]
              :model-value="draftState.state.value.national_id"
              :disabled="!draftState.state.value.isDevAuth"
              type="tel"
              label="ИИН"
              placeholder="000000000000"
              clearable
              @update:model-value="
                draftState.updateDraft({ national_id: $event })
              "
            >
              <template #clearButton>
                <FreeCloseButton class="clear-input-button" />
              </template>
            </FreeInput>
            <FreeInput
              :model-value="draftState.state.value.first_name"
              :disabled="!draftState.state.value.isDevAuth"
              label="Имя"
              placeholder="IVAN"
              clearable
              @update:model-value="
                draftState.updateDraft({ first_name: $event })
              "
            >
              <template #clearButton>
                <FreeCloseButton class="clear-input-button" />
              </template>
            </FreeInput>
            <FreeInput
              :model-value="draftState.state.value.last_name"
              :disabled="!draftState.state.value.isDevAuth"
              label="Фамилия"
              placeholder="IVANOV"
              clearable
              @update:model-value="
                draftState.updateDraft({ last_name: $event })
              "
            >
              <template #clearButton>
                <FreeCloseButton class="clear-input-button" />
              </template>
            </FreeInput>
            <FreeButton
              type="submit"
              class="green-main-button w-full"
              :disabled="!hasChanges || !isValidPhone"
            >
              Применить
            </FreeButton>
            <!-- In dev mode default auth data is set anyway, so we don't need this functionality -->
            <FreeButton
              v-if="!isDevAuth"
              type="button"
              class="gray-button w-full"
              @click="handleLogout"
            >
              Logout
            </FreeButton>
          </form>
        </template>
      </div>
    </template>
  </FreeBottomSheet>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { createStorage } from '@aviata/storage';
  import { useDraftState } from 'shared';
  import {
    aviaMasksOptions,
    FreeBottomSheet,
    FreeButton,
    FreeCloseButton,
    freedomAuthDefaultAuthData,
    freedomAuthMockDataStorageKey,
    FreeHeader,
    FreeInput,
    FreeLoader,
  } from 'freedom-shared';
  import type { Entries } from 'type-fest';

  import { BooleanOption } from '../../entities/boolean-option';
  import { authBottomSheetLib } from './lib';
  import { authBottomSheetApi } from './api';

  type DevAuthState = {
    phone: string;
    national_id: string;
    first_name: string;
    last_name: string;
    isDevAuth: boolean;
  };

  const props = defineProps<{
    name: string;
    isDevAuth: boolean;
  }>();

  const emit = defineEmits<{ onAuthDataChange: [isDevAuth: boolean] }>();

  const phoneMask = aviaMasksOptions.phoneNumber;
  const iinMask = aviaMasksOptions.iin;

  const storage = createStorage(localStorage);

  const getAuthData = () =>
    storage.parse(
      freedomAuthMockDataStorageKey,
      freedomAuthDefaultAuthData,
    ) as Record<string, string>;

  const isLoading = ref(false);

  const defaultState = {
    phone: '',
    national_id: '',
    first_name: '',
    last_name: '',
    isDevAuth: props.isDevAuth,
  };
  const initialState = ref(defaultState);
  const draftState = useDraftState({ initialDraft: defaultState });
  const isValidPhone = computed(() =>
    authBottomSheetLib.isCorrectPhoneNumber(draftState.state.value.phone),
  );

  const getIsStateChanged = (state: Partial<DevAuthState>) => {
    const typedKeys = Object.entries(state) as Entries<DevAuthState>;
    return typedKeys.some(([key, value]) => value !== initialState.value[key]);
  };

  const hasChanges = computed(() => getIsStateChanged(draftState.state.value));
  const hasAuthChanges = computed(() => {
    const { isDevAuth, ...authData } = draftState.state.value;
    return getIsStateChanged(authData);
  });

  const onOpen = () => {
    const authData = getAuthData();
    const newState = {
      phone: authData.phone,
      national_id: authData.national_id,
      first_name: authData.first_name,
      last_name: authData.last_name,
      isDevAuth: props.isDevAuth,
    };
    initialState.value = newState;
    draftState.updateDraft(newState);
  };

  const onClose = () => {
    draftState.resetDraft();
    initialState.value = defaultState;
  };

  const handleLogout = () => {
    isLoading.value = true;
    return authBottomSheetApi
      .logout()
      .then(() => emit('onAuthDataChange', draftState.state.value.isDevAuth))
      .finally(() => (isLoading.value = false));
  };

  const submitForm = () => {
    const authData = getAuthData();
    handleLogout().then(() => {
      const { isDevAuth, ...mockAuthData } = draftState.state.value;

      storage.stringify(freedomAuthMockDataStorageKey, {
        ...authData,
        ...mockAuthData,
      });

      if (hasAuthChanges.value) {
        window.location.reload();
      }
    });
  };
</script>
